import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, session } from '../../../shell/bridge';
import { loadMembers, type Member } from '../../../lib/api';
import { errCode, type AssigneeChoice, type AssignMode, type ChoreItem, type ChoreStreak, type GoogleStatus, type RecurMode } from './types';
import { assigneeLabel, toDateInput, toTimeInput } from './helpers';

/** All Chores panel state, effects and handlers — the hook the old
 *  single-file screen owned (card jJbBjhgw0fk0). Pure move: every piece of
 *  state and every handler is unchanged; index.tsx destructures this and
 *  passes slices down to the section modules as props. */
export function useChores() {
  const me = session();
  const [items, setItems] = useState<ChoreItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [title, setTitle] = useState('');
  const [cadence, setCadence] = useState<'once' | 'recurring'>('once');
  const [freq, setFreq] = useState('weekly');
  const [due, setDue] = useState('');
  /** Optional clock time for the due date — the "Specify Time" field. Empty
   *  keeps the date-only noon sentinel the picker has always sent. */
  const [time, setTime] = useState('');
  /** Assignee chosen at creation: the Me / Partner / Anyone segments. */
  const [assignee, setAssignee] = useState<AssigneeChoice>('anyone');
  /** Which rule owns that assignee (card UeOwZSHfdZCh). */
  const [assignMode, setAssignMode] = useState<AssignMode>('fixed');
  /** Has a person actually chosen a mode this time round? A NEW recurring
   *  chore defaults to 'alternate' — turns need no fairness engine, and the
   *  cheapest fairness win is drift that never starts (card kZX3gsJkKaTH).
   *  The default follows the cadence only until someone picks a mode by hand,
   *  and an edit always counts as picked, so opening an existing chore never
   *  rewrites the rule it was created with. */
  const [assignModePicked, setAssignModePicked] = useState(false);
  /** Which rule a recurring chore follows (card VmoORelj1HuO). */
  const [recurMode, setRecurMode] = useState<RecurMode>('fixed');
  /** Step, in days, for the two N-day rules — held as the field's text so a
   *  half-typed "1" is editable state, not an invalid number. */
  const [every, setEvery] = useState('3');
  /** Seeds picked in the library: several chores can be added in one go. */
  const [picked, setPicked] = useState<string[]>([]);
  /** Row being edited — the composer switches to Save / Cancel while it is
   *  set, so edit reuses every field the create form already has. */
  const [editing, setEditing] = useState<ChoreItem | null>(null);
  /** Delete is two taps: first arms the row, second removes it. */
  const [armedDelete, setArmedDelete] = useState<string | null>(null);
  /** "Add to Calendar & Tasks" — on by default, only offered on builds that
   *  have a Google client configured (the switch is hidden otherwise). */
  const [addToGoogle, setAddToGoogle] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Chore that just got ticked off — drives the one-shot celebration toast. */
  const [celebrated, setCelebrated] = useState<string | null>(null);
  /* Week summary drawer (card QR9phRSRQHNx, founder batch 2026-10-13):
     day | task | responsible | complete for the week ahead, as a right-edge
     pop-out. The three filters are panel state like every other drawer. */
  const [weekOpen, setWeekOpen] = useState(false);
  const [weekDay, setWeekDay] = useState('all');
  const [weekWho, setWeekWho] = useState('all');
  const [weekDone, setWeekDone] = useState('all');
  /* Desktop uplift A6 (card R0Yth1PtWGRg): at the shipped >=1100px token the
     week summary is a persistent right rail beside the list — same matchMedia
     seam Thread, A2 and A3 use. Below the token none of it exists: the
     toggle, the drawer and the phone box tree are the ones that shipped. */
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1100px)').matches);
  /** Every member of the couple (id AND display name — the create form's
   *  segments name the partner). Read from the session cache FIRST, then
   *  refreshed — a solo couple (partner has not joined yet) must still cycle
   *  Anyone ↔ Me rather than dead-end on a tap that goes nowhere (card
   *  4kQn1aaw0rf9). */
  const [members, setMembers] = useState<Member[]>([]);

  /* Reactions + done-notes (card MnorjNDwB3of): one glyph per member, one
     receipt line per completion. The composer is row-scoped — which card is
     getting a note and the draft riding it — because a note attaches to one
     completion, never to the panel. */
  /** Row whose done-note composer is open. */
  const [noteFor, setNoteFor] = useState<string | null>(null);
  /** The draft line for that row. */
  const [noteDraft, setNoteDraft] = useState('');
  /** Set while a react/note POST is in flight — keyed to the row. */
  const [reactBusyId, setReactBusyId] = useState<string | null>(null);

  /* Lifecycle (card xvom08w4BgGT): the "Later" chooser, the cap that stops a
     fourth push, and the couple's list-clear streak. All three are panel
     state, never row state — the chooser and the options card are about the
     list, and the streak has no owner but the couple. */
  /** Row whose Later chooser is open. */
  const [laterFor, setLaterFor] = useState<string | null>(null);
  /** The chore the cap stopped — drives the Reassign / Renegotiate card. */
  const [capped, setCapped] = useState<string | null>(null);
  /** One line of feedback for the chooser / options card. */
  const [laterNote, setLaterNote] = useState<string | null>(null);
  /** The streak celebration line, set once and never by a plain tick-off. */
  const [streakLabel, setStreakLabel] = useState<string | null>(null);
  /** The last clear this panel has already acknowledged. Primed on mount so a
   *  streak fired before the panel opened is history, not news — the toast is
   *  an edge, not a level. */
  const seenClearAt = useRef<number | null>(null);
  const streakPrimed = useRef(false);

  /* Google Calendar + Tasks connect (card hWnpJn7Tfl38) — status, the
     one-shot notice Google hands back, and the two buttons that drive it. */
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [gBusy, setGBusy] = useState(false);
  const [gNote, setGNote] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const data = await api<{ chores: ChoreItem[] }>('/api/chores');
      setItems(data.chores ?? []);
      setFailed(false);
    } catch (e) {
      console.error('[Pear] chores load failed', e);
      setItems([]);
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Live from the room socket: when the partner taps a reaction or writes a
  // done-note, this list refetches so the glyph and receipt land without a
  // reload (card MnorjNDwB3of) — the same hand-off the Requests panel uses
  // for kudos (the DO broadcasts t:'chores'; useThread turns it into this).
  useEffect(() => {
    const onChores = () => void reload();
    window.addEventListener('pear:chores', onChores);
    return () => window.removeEventListener('pear:chores', onChores);
  }, [reload]);

  // The toast is anchored to the viewport above the tab bar (card
  // FktjQ7CT4keo), so the row it celebrates is brought to the middle of the
  // panel after the list reloads — the card the toast is about is never the
  // card sitting underneath it. Re-runs when `items` lands: the Done row only
  // exists once that reload resolves.
  useEffect(() => {
    if (!celebrated) return;
    const row = document.querySelector(`[data-chore-id="${celebrated}"]`);
    if (!row) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
  }, [celebrated, items]);

  // Who is "partner" on the assignee pill. Read once — a member list does not
  // change while the panel is open, and a refetch loop would fight the clicks.
  // Keyed on memberId, not `me`: session() hands back a fresh object every
  // render, so `me` as a dependency re-fired this effect forever and kept
  // re-posting /api/pair/status (same root cause as card iKkKUEi2QZHZ).
  const meKey = me?.memberId ?? '';
  useEffect(() => {
    if (!meKey) return;
    let live = true;
    // The cache is enough to name the other member on the spot: the refresh
    // below only corrects it, and a failed refresh must not take the control
    // away (that is the bug this card reports).
    const cached = loadMembers();
    if (cached.length) setMembers(cached);
    api<{ couple?: { members?: Member[] } }>('/api/pair/status')
      .then(d => {
        if (!live) return;
        const ms = d.couple?.members ?? [];
        if (ms.length) setMembers(ms);
      })
      .catch(() => {/* keep whatever the cache gave us */});
    return () => { live = false; };
  }, [meKey]);

  /* Escape closes the week drawer the way it closes every other overlay. */
  useEffect(() => {
    if (!weekOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setWeekOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [weekOpen]);

  /* The token is a live seam (same contract A2/A3 ship): resizing across it
     swaps the drawer for the rail. A drawer left open at the moment of the
     cross would strand an overlay the toggle no longer controls, so the
     cross closes it — the rail it becomes is already on screen. */
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1100px)');
    const sync = () => {
      setWide(mq.matches);
      if (mq.matches) setWeekOpen(false);
    };
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const loadGoogle = useCallback(async () => {
    try {
      setGoogle(await api<GoogleStatus>('/api/chores/google/status'));
    } catch (e) {
      console.error('[Pear] google connect status failed', e);
      setGoogle(null);
    }
  }, []);

  useEffect(() => {
    void loadGoogle();
  }, [loadGoogle]);

  // Land back from Google: read ?gconnect= once, then take it off the URL so
  // a refresh does not repeat the same notice (same pattern as ?oauth_code=).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('gconnect');
    if (!result) return;
    setGNote(
      result === 'connected'
        ? 'Google connected — chores can now reach that calendar and task list.'
        : result === 'denied'
          ? 'Google link cancelled — nothing was shared.'
          : result === 'expired'
            ? 'That Google link expired — press Connect to start again.'
            : 'The Google Link Did Not Complete — Press Connect to Start Again.',
    );
    params.delete('gconnect');
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }, []);

  const connectGoogle = async () => {
    if (gBusy) return;
    setGBusy(true);
    try {
      const res = await api<{ url: string }>('/api/chores/google/connect', {
        body: { return: window.location.origin },
      });
      if (!res.url) throw new Error('no authorize url');
      // Top-level navigation on purpose: the consent screen is Google's page,
      // not ours, and it redirects straight back to the worker's callback.
      window.location.assign(res.url);
    } catch (e) {
      console.error('[Pear] google connect start failed', e);
      setGNote('Could not start the Google link — try again in a moment.');
      setGBusy(false);
    }
  };

  const disconnectGoogle = async () => {
    if (gBusy) return;
    setGBusy(true);
    try {
      await api('/api/chores/google/disconnect', { body: {} });
      setGNote('Google disconnected — Pear no longer touches that calendar.');
      await loadGoogle();
    } catch (e) {
      console.error('[Pear] google disconnect failed', e);
    } finally {
      setGBusy(false);
    }
  };

  const celebrate = (choreId: string) => {
    setCelebrated(choreId);
    window.setTimeout(
      () => setCelebrated(c => (c === choreId ? null : c)),
      4000,
    );
  };

  // An armed delete disarms itself: "Sure?" is a moment, not a mode, so a
  // row left armed cannot be destroyed by the next stray tap minutes later.
  useEffect(() => {
    if (!armedDelete) return;
    const t = window.setTimeout(() => setArmedDelete(null), 4000);
    return () => window.clearTimeout(t);
  }, [armedDelete]);

  /** The other member, if the couple has one — the create form's segments
   *  name them, and the row's own pill still says "Partner". Solo couples
   *  simply have no partner segment to pick. */
  const otherMember = members.find(m => m.memberId !== (me?.memberId ?? '')) ?? null;
  const partnerName = otherMember?.displayName?.trim() || 'Partner';
  /* Card DfhelObKj9iP (Jonathan 2026-10-30): where the copy refers to the
     partner in a sentence, name her; the bare 'Partner' stays for the pill
     label, where it is a role and not a person. */
  const partnerPhrase = otherMember?.displayName?.trim() || 'your partner';

  /** The N-day step as a usable day count, or null while the field holds
   *  something the API would refuse (0, 4000, half a number). */
  const everyDays = (() => {
    const n = Number(every);
    return Number.isInteger(n) && n >= 1 && n <= 365 ? n : null;
  })();

  /** What the composer is currently describing, as the API wants it. */
  const composerBody = (): Record<string, unknown> | null => {
    const t = title.trim();
    if (!t) return null;
    if (cadence === 'recurring' && recurMode !== 'fixed' && everyDays === null) return null;
    const body: Record<string, unknown> = { title: t, cadence };
    if (cadence === 'recurring') {
      if (recurMode === 'fixed') body.freq = freq;
      else {
        body.recurMode = recurMode;
        body.recurEveryDays = everyDays;
      }
    }
    // Noon stays the date-only sentinel; a picked time rides along on the
    // same timestamp, and hasTime tells the worker WHICH of the two it is —
    // the sentinel is local noon, so only this browser can recognise it and
    // the worker must not guess (card ZgLp6PjCXHEm: a time-blocked chore is
    // a Calendar event, a dated one is a Tasks task).
    // Only a real picker date (`YYYY-MM-DD`) reaches the API: without a date
    // picker the field falls back to free text, and a typed `12/11/26` used
    // to parse into a wrong-but-plausible date. The Add button is already
    // disabled when a time is set with no date, so this is the second net.
    if (/^\d{4}-\d{2}-\d{2}$/.test(due)) {
      const pickedTime = Boolean(time) && /^\d{2}:\d{2}$/.test(time);
      const stamped = new Date(pickedTime ? `${due}T${time}:00` : `${due}T12:00:00`).getTime();
      if (!Number.isNaN(stamped)) {
        body.dueAt = stamped;
        body.hasTime = pickedTime;
      }
    }
    body.assignMode = assignMode;
    // A claim chore is open until someone takes it, and an edit must never
    // release a claim somebody already holds — so the field is left OUT of
    // the request instead of being sent as null (card UeOwZSHfdZCh). The
    // Me / Partner / Anyone row is hidden for that mode anyway.
    if (assignMode !== 'claim') {
      body.assigneeId =
        assignee === 'me' ? (me?.memberId ?? null)
          : assignee === 'partner' ? (otherMember?.memberId ?? null)
            : null;
    }
    if (!editing) body.syncGoogle = addToGoogle;
    return body;
  };

  const resetComposer = () => {
    setTitle('');
    setDue('');
    setTime('');
    setEditing(null);
    setRecurMode('fixed');
    setEvery('3');
    setAssignMode('fixed');
    setAssignModePicked(false);
  };

  /** Add a row, or save the one being edited (card VmoORelj1HuO). */
  const submitChore = async (e: FormEvent) => {
    e.preventDefault();
    const body = composerBody();
    if (!body || adding) return;
    if (editing) {
      // A cleared date is a real edit: the row goes back to "No date" rather
      // than keeping whatever it had. syncGoogle has no edit path by design
      // (the switch is part of the create form), so it is never sent here.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) {
        body.dueAt = null;
        body.hasTime = false;
      }
      delete body.syncGoogle;
    }
    setAdding(true);
    try {
      if (editing) await api(`/api/chores/${editing.choreId}`, { method: 'PATCH', body });
      else await api('/api/chores', { body });
      resetComposer();
      await reload();
    } catch (err) {
      console.error(editing ? '[Pear] edit chore failed' : '[Pear] add chore failed', err);
    } finally {
      setAdding(false);
    }
  };

  /** A seed chip toggles: several can be picked before one Add. */
  const toggleSeed = (seed: string) => {
    setPicked(p => (p.includes(seed) ? p.filter(s => s !== seed) : [...p, seed]));
  };

  /** Add every picked seed as its own one-off chore, under the assignee the
   *  composer already chose — that is what makes a whole week's shelf a
   *  three-tap job instead of fourteen forms. */
  const addSeeds = async () => {
    if (picked.length === 0 || adding) return;
    setAdding(true);
    const assigneeId =
      assignee === 'me' ? (me?.memberId ?? null)
        : assignee === 'partner' ? (otherMember?.memberId ?? null)
          : null;
    try {
      for (const t of picked) {
        // The seeds take the composer's mode too (card UeOwZSHfdZCh) — a
        // claim shelf starts open, a fixed shelf takes the person picked.
        const body: Record<string, unknown> = { title: t, cadence: 'once', assignMode, syncGoogle: addToGoogle };
        if (assignMode !== 'claim') body.assigneeId = assigneeId;
        await api('/api/chores', { body });
      }
      setPicked([]);
      await reload();
    } catch (err) {
      console.error('[Pear] add seeds failed', err);
      await reload();
    } finally {
      setAdding(false);
    }
  };

  /** Load a row into the composer so its fields can be changed. */
  const beginEdit = (chore: ChoreItem) => {
    setEditing(chore);
    setTitle(chore.title);
    setCadence(chore.cadence);
    setFreq(chore.freq ?? 'weekly');
    setRecurMode(chore.recurMode ?? 'fixed');
    setEvery(String(chore.recurEveryDays ?? 3));
    setDue(toDateInput(chore.dueAt));
    setTime(toTimeInput(chore.dueAt));
    setAssignMode(chore.assignMode ?? 'fixed');
    setAssignModePicked(true);
    setAssignee(
      chore.assigneeId == null ? 'anyone'
        : chore.assigneeId === (me?.memberId ?? '') ? 'me'
          : 'partner',
    );
    setArmedDelete(null);
    const input = document.getElementById('ch-input');
    input?.scrollIntoView({ block: 'center' });
    (input as HTMLInputElement | null)?.focus();
  };

  /** Two taps to remove a row: the first arms it and says so, the second
   *  means it. A stray tap must never delete someone's chore. */
  const removeChore = async (chore: ChoreItem) => {
    if (busyId) return;
    if (armedDelete !== chore.choreId) {
      setArmedDelete(chore.choreId);
      return;
    }
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}`, { method: 'DELETE' });
      setArmedDelete(null);
      if (editing?.choreId === chore.choreId) resetComposer();
      await reload();
    } catch (err) {
      console.error('[Pear] delete chore failed', err);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** Assignee pill cycles me → partner → anyone: one control, three states.
   *  Solo (no second member yet) it is a two-state cycle, me ↔ anyone — every
   *  tap must move the pill, so the assignment always sticks.
   *
   *  The row's own mode decides what a tap MEANS (card UeOwZSHfdZCh): fixed
   *  and alternate cycle the pointer, claim takes the row when it is free,
   *  lets go of a hold that is yours, and does nothing when the partner
   *  holds it — a claim is theirs to release, never to be taken back. */
  const tapAssignee = async (chore: ChoreItem) => {
    if (busyId || !me) return;
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'claim') {
      if (chore.assigneeId != null && chore.assigneeId !== me.memberId) return;
      setBusyId(chore.choreId);
      try {
        if (chore.assigneeId == null) await api(`/api/chores/${chore.choreId}/claim`, { body: {} });
        else await api(`/api/chores/${chore.choreId}/assign`, { body: { assigneeId: null } });
        await reload();
      } catch (err) {
        console.error('[Pear] claim chore failed', err);
        await reload();
      } finally {
        setBusyId(null);
      }
      return;
    }
    const other = members.find(m => m.memberId !== me.memberId)?.memberId ?? null;
    const next = chore.assigneeId == null
      ? me.memberId
      : chore.assigneeId === me.memberId
        ? other // null while solo — drop straight back to Anyone
        : null;
    if (next === chore.assigneeId) return;
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/assign`, { body: { assigneeId: next } });
      await reload();
    } catch (err) {
      console.error('[Pear] assign chore failed', err);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const complete = async (chore: ChoreItem) => {
    if (busyId) return;
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/complete`, { body: {} });
      celebrate(chore.choreId);
      await reload();
      // Only a completion can clear the list, so this is the only call site
      // that can turn a streak into a celebration (card xvom08w4BgGT).
      await checkStreak();
    } catch (e) {
      console.error('[Pear] complete chore failed', e);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** Tap a reaction glyph (card MnorjNDwB3of) — a toggle: the same glyph
   *  again clears it, a different one replaces it. One per member, so the
   *  pair's two taps are the whole thread. Any member may react to any row. */
  const react = async (chore: ChoreItem, glyph: string) => {
    if (reactBusyId) return;
    setReactBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/react`, { body: { glyph } });
      await reload();
    } catch (e) {
      console.error('[Pear] chore reaction failed', e);
    } finally {
      setReactBusyId(null);
    }
  };

  /** Save the warm receipt (card MnorjNDwB3of). Whether the note rides the
   *  tick or replaces an existing one is decided by COMPLETION STATE, not by
   *  `status` — a recurring chore stays 'open' forever after a tick, so
   *  branching on status would tick it AGAIN (advancing its date and
   *  rotating its assignee) just to edit last round's note (sol review
   *  2026-10-16). A row that has never been completed completes WITH the
   *  note; anything already ticked just replaces the stored line. */
  const saveNote = async (chore: ChoreItem) => {
    if (busyId || reactBusyId) return;
    const note = noteDraft.trim();
    setBusyId(chore.choreId);
    try {
      if (chore.completedAt == null) {
        await api(`/api/chores/${chore.choreId}/complete`, { body: { note } });
        celebrate(chore.choreId);
      } else {
        await api(`/api/chores/${chore.choreId}/done-note`, { body: { note } });
      }
      setNoteFor(null);
      setNoteDraft('');
      await reload();
    } catch (e) {
      console.error('[Pear] chore done-note failed', e);
    } finally {
      setBusyId(null);
    }
  };


  /** Open the receipt composer on a row, seeded with whatever note it
   *  already carries so a replace starts from the current line (MnorjNDwB3of). */
  const openNote = (chore: ChoreItem) => {
    setNoteFor(chore.choreId);
    setNoteDraft(chore.doneNote ?? '');
  };


  /** Read the couple's streak and celebrate a clear this panel has not seen.
   *  The first read only primes the ref: a streak that fired earlier in the
   *  week is history, and re-announcing it on every visit would be the nag
   *  this card exists to avoid. Never throws — a celebration is not a
   *  dependency, and a failed read must not break a tick-off. */
  const checkStreak = useCallback(async () => {
    try {
      const s = await api<ChoreStreak>('/api/chores/streak');
      if (!streakPrimed.current) {
        streakPrimed.current = true;
        seenClearAt.current = s.lastClearAt;
        return;
      }
      if (s.lastClearAt != null && s.lastClearAt !== seenClearAt.current) {
        seenClearAt.current = s.lastClearAt;
        setStreakLabel(
          s.streak > 1
            ? `Whole list clear — ${s.streak} weeks running.`
            : 'Whole list clear — the streak starts here.',
        );
      }
    } catch (e) {
      console.error('[Pear] streak read failed', e);
    }
  }, []);

  // Prime the ref before the first tick can race it.
  useEffect(() => {
    void checkStreak();
  }, [checkStreak]);

  // A chore deleted while its chooser or options card is open must not leave
  // a control pointing at a row that no longer exists.
  useEffect(() => {
    if (items === null) return;
    if (capped && !items.some(c => c.choreId === capped)) setCapped(null);
    if (laterFor && !items.some(c => c.choreId === laterFor)) setLaterFor(null);
  }, [items, capped, laterFor]);

  /** Snooze to a chosen time, or defer to the next natural slot. The 4th push
   *  in an occurrence is refused by the server with snooze_cap, and THAT is
   *  what opens the options card — the cap is enforced where the data is, so
   *  two devices cannot each take three pushes. */
  const pushLater = async (chore: ChoreItem, kind: 'snooze' | 'defer', returnAt?: number) => {
    if (busyId) return;
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/${kind}`, {
        body: kind === 'snooze' ? { returnAt } : {},
      });
      setLaterFor(null);
      setLaterNote(null);
      await reload();
    } catch (e) {
      if (errCode(e) === 'snooze_cap') {
        setCapped(chore.choreId);
        setLaterFor(null);
        setLaterNote(null);
      } else {
        setLaterNote('Could not move that one — try again.');
      }
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** Second half of the cap: renegotiate turns the stuck chore into an ask,
   *  which is where a disagreement about whose job it is belongs — the Life
   *  lane, where it can be accepted, countered or declined (card xvom08w4BgGT). */
  const renegotiate = async (chore: ChoreItem) => {
    if (busyId) return;
    setBusyId(chore.choreId);
    try {
      await api('/api/requests', {
        body: { text: `Can we sort out who does “${chore.title}”?`, lane: 'life' },
      });
      setCapped(null);
      setLaterNote(`Asked ${partnerPhrase} — it is sitting in Needs you.`);
    } catch (e) {
      console.error('[Pear] renegotiate request failed', e);
      setLaterNote('Could not send that ask — try again.');
    } finally {
      setBusyId(null);
    }
  };


  const open = items?.filter(c => c.status === 'open') ?? [];
  const done = items?.filter(c => c.status === 'done') ?? [];

  /* The week the drawer shows: everything due before the end of tomorrow's
     seven-day window, overdue rows included — a late chore is exactly what
     the at-a-glance view exists to surface. Undated chores have no day to
     hang on, so they stay out of the table. */
  const DAY_MS = 24 * 3_600_000;
  const weekStart = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  })();
  const weekRows = (items ?? [])
    .filter(c => c.dueAt != null && c.dueAt < weekStart + 7 * DAY_MS)
    .sort((a, b) => (a.dueAt ?? 0) - (b.dueAt ?? 0));
  const weekDayKey = (c: ChoreItem): string =>
    c.dueAt == null ? '' : new Date(c.dueAt).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const weekDays = [...new Set(weekRows.map(weekDayKey))];
  const weekFiltered = weekRows.filter(c => {
    if (weekDay !== 'all' && weekDayKey(c) !== weekDay) return false;
    if (weekWho !== 'all' && assigneeLabel(c, me) !== weekWho) return false;
    if (weekDone !== 'all' && c.status !== weekDone) return false;
    return true;
  });
  /** The chore the toast is celebrating — its assignment is what the toast
   *  reads back, so "Anyone" / "Me" / "Partner" is honoured after tick-off
   *  instead of a bare "Chore done" (card 4kQn1aaw0rf9). */
  const celebratedChore = items?.find(c => c.choreId === celebrated) ?? null;
  /** The chore the cap stopped — null once it is deleted or dismissed, which
   *  is exactly when the options card should stop rendering. */
  const cappedChore = items?.find(c => c.choreId === capped) ?? null;

  /** A clock with no day to hang on cannot be stored, so Add waits for the
   *  date instead of silently dropping the time the user just picked. The
   *  date field is the only one of the two that can stand alone. */
  const timeWithoutDate = time !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(due);


  return {
    me, items, failed, members, otherMember, partnerName, partnerPhrase,
    open, done, celebratedChore, cappedChore,
    title, setTitle, cadence, setCadence, freq, setFreq, due, setDue, time, setTime,
    assignee, setAssignee, assignMode, setAssignMode, assignModePicked, setAssignModePicked,
    recurMode, setRecurMode, every, setEvery, editing, addToGoogle, setAddToGoogle,
    adding, everyDays, timeWithoutDate, submitChore, resetComposer, beginEdit,
    picked, setPicked, toggleSeed, addSeeds,
    busyId, armedDelete, removeChore, tapAssignee, complete,
    reactBusyId, react, noteFor, setNoteFor, noteDraft, setNoteDraft, saveNote, openNote,
    laterFor, setLaterFor, capped, setCapped, laterNote, setLaterNote, streakLabel, setStreakLabel,
    pushLater, renegotiate, celebrated,
    wide, weekOpen, setWeekOpen, weekDay, setWeekDay, weekWho, setWeekWho, weekDone, setWeekDone,
    weekRows, weekDayKey, weekDays, weekFiltered,
    google, gBusy, gNote, connectGoogle, disconnectGoogle,
  };
}
